import type { AnchorHTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";
import { ActiveStripe } from "./active-stripe";
import { NotificationDot } from "./notification-dot";

export interface NavItemProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children"> {
	icon?: ReactNode;
	label: ReactNode;
	active?: boolean;
	notification?: number | string;
	/**
	 * Чем отмечен активный раздел.
	 * `plate` — тёмная 48px плашка под глифом, как в макетах витрины. Это
	 *   дефолт и единственное, что использует витрина.
	 * `stripe` — общий `<ActiveStripe />`, канонический маркер «где я» из
	 *   бренд-брифа. Приложения его сейчас не выбирают: витрина рисует плашку
	 *   по макетам, а меню админки — другой геометрии (40px без плашки под
	 *   глифом) и собрано своей строкой. Вариант оставлен не «на всякий
	 *   случай»: он показан в `__preview__/kitchen-sink`, и полоса в нём та же
	 *   самая, что у админки, — общая, а не вторая копия.
	 *
	 * Полоса и плашка вместе не ставятся — два маркера одного состояния
	 * читаются как два разных состояния.
	 */
	marker?: "plate" | "stripe";
}

/**
 * Строка боковой навигации: 220×56, паддинг 4, зазор 12 между 48px плашкой
 * глифа и подписью 15px Geist Mono CAPS.
 *
 * Компонент не знает про роутер: активность приходит пропом, переход — обычным
 * href. Так его можно вставить и в витрину на TanStack Router, и в чужой сервис.
 */
export function NavItem({
	icon,
	label,
	active = false,
	notification,
	marker = "plate",
	className,
	...rest
}: NavItemProps) {
	const plateActive = active && marker === "plate";
	return (
		<a
			aria-current={active ? "page" : undefined}
			className={cn(
				"iec-press-glyph relative flex h-14 items-center gap-3 overflow-hidden rounded-control p-1 text-fg",
				"transition-colors duration-150 ease-smooth",
				"hover:bg-black/4",
				"focus-ring",
				className,
			)}
			{...rest}
		>
			{active && marker === "stripe" ? <ActiveStripe /> : null}
			<span
				className={cn(
					"relative inline-flex size-12 shrink-0 items-center justify-center rounded-control bg-sunken",
					plateActive && "text-warm",
				)}
			>
				<span aria-hidden className={cn("iec-plate", plateActive && "iec-plate-on")} />
				<span className={cn("relative", plateActive && "iec-glyph-pop")}>{icon}</span>
				{notification != null ? (
					<NotificationDot
						value={notification}
						className="absolute top-[9px] left-[27px]"
						ringClassName={plateActive ? "ring-ink" : "ring-sunken"}
					/>
				) : null}
			</span>
			{/*
			  Подпись переносится на вторую строку и обрезается многоточием на
			  третьей. Высота строки 56px вмещает ровно две строки 14px/1.4, а
			  `overflow-hidden` у ссылки обрезал бы третью посередине буквы —
			  `line-clamp-2` делает это честным многоточием.
			  `min-w-0` обязателен: без него flex-элемент не сжимается ниже
			  ширины содержимого, и перенос не срабатывает вовсе.
			*/}
			<span className="t-btn line-clamp-2 min-w-0 break-words hyphens-auto">{label}</span>
		</a>
	);
}
